import { type ReactNode } from 'react'
import { type StyleProp, type ViewProps, type ViewStyle } from 'react-native'
import { RADIUS } from '@/lib/brand'
import { GlassSurface } from './GlassSurface'

type GlassSheetProps = {
  children: ReactNode
  style?: StyleProp<ViewStyle>
  contentStyle?: StyleProp<ViewStyle>
  radius?: number
  testID?: string
  accessibilityRole?: ViewProps['accessibilityRole']
  accessibilityLabel?: ViewProps['accessibilityLabel']
  accessibilityState?: ViewProps['accessibilityState']
  accessibilityViewIsModal?: ViewProps['accessibilityViewIsModal']
  importantForAccessibility?: ViewProps['importantForAccessibility']
}

/**
 * Shared material for dialogs, menus and bottom sheets.
 *
 * A sheet is an independent plane, not an opaque white rectangle. This is the
 * single place where web receives an intentionally bounded backdrop blur and
 * iOS receives native Liquid Glass; Android retains the lightweight composite
 * edge so it stays fluid above a keyboard, camera or Mapbox view.
 */
export function GlassSheet({
  children,
  style,
  contentStyle,
  radius = RADIUS.xl,
  testID,
  accessibilityRole,
  accessibilityLabel,
  accessibilityState,
  accessibilityViewIsModal,
  importantForAccessibility,
}: GlassSheetProps) {
  return (
    <GlassSurface
      tone="regular"
      visualRole="sheet"
      radius={radius}
      style={style}
      contentStyle={contentStyle}
      testID={testID}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      accessibilityViewIsModal={accessibilityViewIsModal}
      importantForAccessibility={importantForAccessibility}
    >
      {children}
    </GlassSurface>
  )
}
